<script setup lang="ts">
import { computed } from 'vue';
import { AlertTriangleIcon } from '@lucide/vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { isBrokenTableauLink, type TableauLinkStatus } from '@/types/TableauDashboard';
import { filters } from '@/utilities/Filters';

const props = defineProps<{
  status?: TableauLinkStatus | null;
  checkedAt?: string | null;
}>();

const reasons: Partial<Record<TableauLinkStatus, string>> = {
  WORKBOOK_NOT_FOUND: 'The workbook in this URL is not on Tableau. It may have been deleted, renamed or republished under a new name.',
  VIEW_NOT_FOUND: 'The workbook is on Tableau, but the view in this URL is not. It may have been renamed or removed.',
  INVALID_URL: 'This URL is not a Tableau view URL (expected …/views/<workbook>/<view>).',
  WRONG_HOST: 'This URL points at a different Tableau server than this environment uses.',
};

const label = computed(() => (props.status === 'WRONG_HOST' ? 'Wrong Tableau server' : 'Not found on Tableau'));

const tooltip = computed(() => {
  const reason = (props.status && reasons[props.status]) ?? '';
  const checked = props.checkedAt ? ` Last checked ${filters.date(props.checkedAt)}.` : '';
  return `${reason}${checked} Edit the card to point at the current dashboard.`;
});
</script>

<template>
  <Tooltip
    v-if="isBrokenTableauLink(status)"
    :text="tooltip"
    position="bottom"
    class="shrink-0">
    <span
      class="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs whitespace-nowrap text-amber-800"
      data-testid="tableau-link-broken">
      <AlertTriangleIcon :size="12" />{{ label }}
    </span>
  </Tooltip>
</template>
